import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { LearningTimeResponseSchema } from './step-time.schema.js';
extendZodWithOpenApi(z);
export const StreakSchema = z
    .object({
    currentStreak: z.number().openapi({
        description: 'Current consecutive day streak',
        example: 7,
    }),
    longestStreak: z.number().openapi({
        description: 'All-time longest streak',
        example: 14,
    }),
    lastActivityDate: z.string().nullable().openapi({
        description: 'Last date a step was completed (YYYY-MM-DD in user timezone)',
        example: '2026-02-23',
    }),
    weekDays: z
        .array(z.boolean())
        .length(7)
        .openapi({
        description: 'Activity for each day Mon(0) to Sun(6) in the current week',
        example: [true, true, true, true, true, false, false],
    }),
    daysToNewRecord: z.number().openapi({
        description: 'Days needed to beat the longest streak (0 if already at or past record)',
        example: 3,
    }),
})
    .openapi('Streak');
export const MasteryLevelSchema = z
    .enum(['none', 'introduced', 'practised', 'confident', 'mastered'])
    .openapi('MasteryLevel');
export const FocusAreaSchema = z
    .object({
    tagId: z.string().openapi({ example: '550e8400-e29b-41d4-a716-446655440000' }),
    tagName: z.string().openapi({ example: 'cardiology' }),
    displayName: z.string().nullable().openapi({ example: 'Cardiology' }),
    masteryLevel: MasteryLevelSchema.openapi({
        description: 'Mastery level bucket computed by the mastery engine',
        example: 'practised',
    }),
    masteryPct: z.number().openapi({
        description: 'Mastery score as a percentage (0-100)',
        example: 42,
    }),
    responseCount: z.number().openapi({
        description: 'Number of responses contributing to this focus area in the window',
        example: 18,
    }),
    lastStudiedAt: z.string().nullable().openapi({
        description: 'Date of the most recent response for this focus area (YYYY-MM-DD)',
        example: '2026-04-17',
    }),
})
    .openapi('FocusArea');
export const WindowedRetentionSchema = z
    .object({
    pct: z.number().int().min(0).max(100).openapi({
        description: 'Windowed mastery aggregate (0-100)',
        example: 78,
    }),
    prevPct: z.number().int().min(0).max(100).nullable().openapi({
        description: 'Previous-period retention; null when no prior responses',
        example: 73,
    }),
    deltaPct: z.number().int().nullable().openapi({
        description: 'pct - prevPct; null when prevPct is null',
        example: 5,
    }),
})
    .openapi('WindowedRetention');
export const PerformanceStatsSchema = z
    .object({
    accuracyPct: z.number().nullable().openapi({
        description: 'Percentage of questions answered correctly in the last 7 days',
        example: 84,
    }),
    accuracyDeltaPct: z.number().nullable().openapi({
        description: 'Change in accuracy vs previous 7 days (positive = improvement)',
        example: 3,
    }),
    questionsAnswered: z.number().openapi({
        description: 'Total questions answered in the last 7 days',
        example: 42,
    }),
    focusAreas: z.array(FocusAreaSchema).openapi({
        description: 'Hierarchy-aware focus areas sorted by mastery ascending (weakest first), up to 10',
    }),
    retention: WindowedRetentionSchema.openapi({
        description: 'Windowed mastery aggregate. Drives the dashboard "Retention" stat with previous-period comparison.',
    }),
})
    .openapi('PerformanceStats');
export const StatsResponseSchema = z
    .object({
    lessonsThisWeek: z.number().openapi({
        description: 'Count of unique new lessons completed in the last 7 days',
        example: 5,
    }),
    lessonsThisWeekPrevious: z.number().openapi({
        description: 'Count of unique new lessons completed in the equal-length prior window',
        example: 4,
    }),
    streak: StreakSchema,
    learningTime: LearningTimeResponseSchema,
    performance: PerformanceStatsSchema,
})
    .openapi('StatsResponse');
//# sourceMappingURL=stats.schema.js.map